import { createContext, type ReactNode, useContext, useMemo, useReducer } from 'react';
import type { Bill } from '@/interfaces/Bill';
import { calculateTipToPay } from '@/utils/calculateTipToPay';
import formatNumber from '@/utils/formatNumber';

const HARDCODED_DEFAULT_TIP_PERCENTAGE = 7;

type SplitType = 'SPLIT_EQUALLY' | 'SPLIT_BY_ITEMS' | null;

// State interface
interface CheckoutState {
  // Financial state
  billTotal: number;
  baseToPay: number;
  tipToPay: number;
  selectedTipPercentage: number;
  numberOfSplits: number;
  payingFor: number;

  // Active split mode: null = no split, set to the mode when a split is confirmed/joined
  splitType: SplitType;
  // Confirmed multiset of selected lineItemIds (duplicates allowed, e.g. [coke, coke])
  selectedLineItemIds: string[];

  // Split settings (pending user confirmation)
  pendingSplitSettings: {
    totalPeople: number;
    payingFor: number;
  } | null;

  paymentMethod: string | null;
  isProcessing: boolean;
  isSplitBillOpen: boolean;
  isServiceFeeInfoOpen: boolean;
  isCustomTipOpen: boolean;
  customTipAmount: number | null;
}

interface ComputedCheckoutState extends CheckoutState {
  yourShare: number;
  isValid: boolean;
  totalAmount: number;
}

type CheckoutAction =
  | { type: 'INIT_BILL'; payload: { baseTotal: number; tipAmount: number } }
  | { type: 'CHANGE_TIP'; payload: { amount: number; percentage: number } }
  | {
      type: 'CHANGE_SHARE';
      payload: { yourShare: number; yourTip: number; totalPeople: number; payingFor: number };
    }
  | { type: 'TOGGLE_SPLIT_BILL' }
  | { type: 'DISMISS_SPLIT_BILL' }
  | { type: 'REMOVE_SPLIT' }
  | {
      type: 'CLOSE_SPLIT_BILL_WITH_TIP';
      payload: { onePersonSplit: number; tipToPay: number; isCustomTip?: boolean };
    }
  | { type: 'SET_PAYMENT_METHOD'; payload: string }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'TOGGLE_SERVICE_FEE_INFO' }
  | { type: 'TOGGLE_CUSTOM_TIP' }
  | { type: 'SET_CUSTOM_TIP'; payload: number }
  | { type: 'RESET_TIP_TO_DEFAULT' }
  | { type: 'RESET_STATE' }
  | {
      type: 'INIT_SPLIT';
      payload: { numberOfSplits: number; payingFor: number; baseToPay: number; tipToPay: number };
    }
  | { type: 'SAVE_SPLIT_SETTINGS'; payload: { totalPeople: number; payingFor: number } }
  | { type: 'CONFIRM_SPLIT_SETTINGS' }
  | {
      type: 'CONFIRM_ITEM_SPLIT';
      payload: { selectedLineItemIds: string[]; baseToPay: number; tipToPay: number };
    }
  | { type: 'JOIN_ITEMS_SPLIT' };

const initialState: CheckoutState = {
  billTotal: 0,
  baseToPay: 0,
  tipToPay: 0,
  selectedTipPercentage: HARDCODED_DEFAULT_TIP_PERCENTAGE,
  numberOfSplits: 0,
  payingFor: 0,
  splitType: null,
  selectedLineItemIds: [],
  paymentMethod: null,
  isProcessing: false,
  isSplitBillOpen: false,
  isServiceFeeInfoOpen: false,
  isCustomTipOpen: false,
  customTipAmount: null,
  pendingSplitSettings: null,
};

const calculateYourShare = (state: CheckoutState): number => {
  if (state.numberOfSplits > 1) {
    return state.baseToPay;
  }
  return state.baseToPay;
};

const validateState = (state: CheckoutState): boolean => {
  return state.baseToPay > 0 && state.tipToPay >= 0;
};

const calculateTotalAmount = (state: CheckoutState): number => {
  return state.baseToPay + state.tipToPay;
};

// Reducer function
const checkoutReducer = (state: CheckoutState, action: CheckoutAction): CheckoutState => {
  switch (action.type) {
    case 'INIT_BILL':
      return {
        ...state,
        billTotal: action.payload.baseTotal,
        baseToPay: action.payload.baseTotal,
        tipToPay: action.payload.tipAmount,
        numberOfSplits: 0,
        payingFor: 0,
        isSplitBillOpen: false,
      };

    case 'CHANGE_TIP':
      return {
        ...state,
        tipToPay: action.payload.amount,
        selectedTipPercentage: action.payload.percentage,
        customTipAmount: null, // Reset custom tip when selecting percentage-based tip
      };

    case 'CHANGE_SHARE':
      return {
        ...state,
        baseToPay: Number(formatNumber(action.payload.yourShare)),
        tipToPay: action.payload.yourTip,
        payingFor: action.payload.payingFor,
        numberOfSplits: action.payload.totalPeople,
        // Preserve customTipAmount and selectedTipPercentage if they exist
        customTipAmount: state.customTipAmount,
        selectedTipPercentage: state.selectedTipPercentage,
      };

    case 'TOGGLE_SPLIT_BILL':
      return {
        ...state,
        isSplitBillOpen: !state.isSplitBillOpen,
      };

    case 'DISMISS_SPLIT_BILL':
      // Close the sheet only — a confirmed split (mode, selection, amounts) survives dismissal
      return {
        ...state,
        isSplitBillOpen: false,
      };

    case 'REMOVE_SPLIT':
      return {
        ...state,
        payingFor: 1,
        isSplitBillOpen: false,
        baseToPay: state.billTotal,
        // The active tip was computed against the split share; restore it against the full bill
        tipToPay:
          state.customTipAmount ?? calculateTipToPay(state.billTotal, state.selectedTipPercentage),
        numberOfSplits: 0,
        splitType: null,
        selectedLineItemIds: [],
      };

    case 'CLOSE_SPLIT_BILL_WITH_TIP':
      // Auto-join of an established equal split — call sites must guard on
      // splitDetails.type === 'SPLIT_EQUALLY' (an items split has no equal share to join)
      return {
        ...state,
        payingFor: 1,
        tipToPay: action.payload.tipToPay,
        isSplitBillOpen: false,
        baseToPay: action.payload.onePersonSplit,
        numberOfSplits: 0,
        // Keep custom tip if it was a custom tip, otherwise reset to null
        customTipAmount: action.payload.isCustomTip ? action.payload.tipToPay : null,
        splitType: 'SPLIT_EQUALLY',
        selectedLineItemIds: [],
      };

    case 'SET_PAYMENT_METHOD':
      return {
        ...state,
        paymentMethod: action.payload,
      };

    case 'SET_PROCESSING':
      return {
        ...state,
        isProcessing: action.payload,
      };

    case 'TOGGLE_SERVICE_FEE_INFO':
      return {
        ...state,
        isServiceFeeInfoOpen: !state.isServiceFeeInfoOpen,
      };

    case 'TOGGLE_CUSTOM_TIP':
      return {
        ...state,
        isCustomTipOpen: !state.isCustomTipOpen,
      };

    case 'SET_CUSTOM_TIP':
      return {
        ...state,
        tipToPay: action.payload,
        selectedTipPercentage: 0,
        customTipAmount: action.payload,
        isCustomTipOpen: false,
      };

    case 'RESET_TIP_TO_DEFAULT':
      return {
        ...state,
        customTipAmount: null,
        isCustomTipOpen: false,
      };

    case 'RESET_STATE':
      return { ...initialState };

    case 'INIT_SPLIT':
      return {
        ...state,
        numberOfSplits: action.payload.numberOfSplits,
        payingFor: action.payload.payingFor,
        baseToPay: action.payload.baseToPay,
        tipToPay: action.payload.tipToPay,
        // Auto-joining an existing equal split
        splitType: 'SPLIT_EQUALLY',
      };

    case 'SAVE_SPLIT_SETTINGS':
      return {
        ...state,
        pendingSplitSettings: {
          totalPeople: action.payload.totalPeople,
          payingFor: action.payload.payingFor,
        },
      };

    case 'CONFIRM_SPLIT_SETTINGS':
      return {
        ...state,
        numberOfSplits: state.pendingSplitSettings?.totalPeople || 0,
        payingFor: state.pendingSplitSettings?.payingFor || 1,
        pendingSplitSettings: null,
        isSplitBillOpen: false,
        splitType: 'SPLIT_EQUALLY',
      };

    case 'CONFIRM_ITEM_SPLIT':
      return {
        ...state,
        splitType: 'SPLIT_BY_ITEMS',
        // Confirmed multiset — duplicates preserved (count-based locking)
        selectedLineItemIds: action.payload.selectedLineItemIds,
        baseToPay: Number(formatNumber(action.payload.baseToPay)),
        tipToPay: action.payload.tipToPay,
        // Items split is not an N-way equal split — no "split by N" rendering, paying for self only
        numberOfSplits: 0,
        payingFor: 1,
        isSplitBillOpen: false,
      };

    case 'JOIN_ITEMS_SPLIT':
      // An items split established elsewhere (another payer, or this payer in a
      // previous session) — lock the mode with nothing selected. baseToPay 0 keeps
      // the payment widget hidden until a selection is confirmed: an intent without
      // lineItems on an items-split order would be accepted by the backend and
      // over-pay the bill (BE-2 caveat, ADR 0003). The sheet opens on the picker so
      // the joining payer starts by choosing their items — the items counterpart of
      // the equal flow's automatic one-person share.
      return {
        ...state,
        splitType: 'SPLIT_BY_ITEMS',
        selectedLineItemIds: [],
        baseToPay: 0,
        tipToPay: 0,
        numberOfSplits: 0,
        payingFor: 1,
        isSplitBillOpen: true,
      };

    default:
      return state;
  }
};

interface CheckoutContextType {
  state: ComputedCheckoutState;
  dispatch: React.Dispatch<CheckoutAction>;
  actions: {
    initBill: (baseTotal: number, tipAmount: number) => void;
    changeTip: (amount: number, percentage: number) => void;
    changeShare: (
      yourShare: number,
      yourTip: number,
      totalPeople: number,
      payingFor: number
    ) => void;
    toggleSplitBill: () => void;
    dismissSplitBill: () => void;
    removeSplit: () => void;
    closeSplitBillWithTip: (
      onePersonSplit: number,
      tipToPay: number,
      isCustomTip?: boolean
    ) => void;
    setPaymentMethod: (paymentMethod: string) => void;
    setProcessing: (isProcessing: boolean) => void;
    toggleServiceFeeInfo: () => void;
    toggleCustomTip: () => void;
    setCustomTip: (amount: number) => void;
    resetTipToDefault: () => void;
    resetState: () => void;
    initSplit: (
      numberOfSplits: number,
      payingFor: number,
      baseToPay: number,
      tipToPay: number
    ) => void;
    saveSplitSettings: (totalPeople: number, payingFor: number) => void;
    confirmSplitSettings: () => void;
    confirmItemSplit: (selectedLineItemIds: string[], baseToPay: number, tipToPay: number) => void;
    joinItemsSplit: () => void;
  };
}

const CheckoutContext = createContext<CheckoutContextType | null>(null);

interface CheckoutProviderProps {
  children: ReactNode;
  bill?: Bill;
}

export const CheckoutProvider = ({ children }: CheckoutProviderProps) => {
  const [state, dispatch] = useReducer(checkoutReducer, initialState);

  const computedState: ComputedCheckoutState = useMemo(
    () => ({
      ...state,
      yourShare: calculateYourShare(state),
      isValid: validateState(state),
      totalAmount: calculateTotalAmount(state),
    }),
    [state]
  );

  const actions = useMemo(
    () => ({
      initBill: (baseTotal: number, tipAmount: number) => {
        dispatch({ type: 'INIT_BILL', payload: { baseTotal, tipAmount } });
      },
      changeTip: (amount: number, percentage: number) => {
        dispatch({ type: 'CHANGE_TIP', payload: { amount, percentage } });
      },
      changeShare: (yourShare: number, yourTip: number, totalPeople: number, payingFor: number) => {
        dispatch({ type: 'CHANGE_SHARE', payload: { yourShare, yourTip, totalPeople, payingFor } });
      },
      toggleSplitBill: () => {
        dispatch({ type: 'TOGGLE_SPLIT_BILL' });
      },
      dismissSplitBill: () => {
        dispatch({ type: 'DISMISS_SPLIT_BILL' });
      },
      removeSplit: () => {
        dispatch({ type: 'REMOVE_SPLIT' });
      },
      closeSplitBillWithTip: (onePersonSplit: number, tipToPay: number, isCustomTip?: boolean) => {
        dispatch({
          type: 'CLOSE_SPLIT_BILL_WITH_TIP',
          payload: { onePersonSplit, tipToPay, isCustomTip },
        });
      },
      setPaymentMethod: (paymentMethod: string) => {
        dispatch({ type: 'SET_PAYMENT_METHOD', payload: paymentMethod });
      },
      setProcessing: (isProcessing: boolean) => {
        dispatch({ type: 'SET_PROCESSING', payload: isProcessing });
      },
      toggleServiceFeeInfo: () => {
        dispatch({ type: 'TOGGLE_SERVICE_FEE_INFO' });
      },
      toggleCustomTip: () => {
        dispatch({ type: 'TOGGLE_CUSTOM_TIP' });
      },
      setCustomTip: (amount: number) => {
        dispatch({ type: 'SET_CUSTOM_TIP', payload: amount });
      },
      resetTipToDefault: () => {
        dispatch({ type: 'RESET_TIP_TO_DEFAULT' });
      },
      resetState: () => {
        dispatch({ type: 'RESET_STATE' });
      },
      initSplit: (
        numberOfSplits: number,
        payingFor: number,
        baseToPay: number,
        tipToPay: number
      ) => {
        dispatch({
          type: 'INIT_SPLIT',
          payload: { numberOfSplits, payingFor, baseToPay, tipToPay },
        });
      },
      saveSplitSettings: (totalPeople: number, payingFor: number) => {
        dispatch({
          type: 'SAVE_SPLIT_SETTINGS',
          payload: { totalPeople, payingFor },
        });
      },
      confirmSplitSettings: () => {
        dispatch({ type: 'CONFIRM_SPLIT_SETTINGS' });
      },
      confirmItemSplit: (selectedLineItemIds: string[], baseToPay: number, tipToPay: number) => {
        dispatch({
          type: 'CONFIRM_ITEM_SPLIT',
          payload: { selectedLineItemIds, baseToPay, tipToPay },
        });
      },
      joinItemsSplit: () => {
        dispatch({ type: 'JOIN_ITEMS_SPLIT' });
      },
    }),
    []
  );

  const contextValue: CheckoutContextType = useMemo(
    () => ({
      state: computedState,
      dispatch,
      actions,
    }),
    [computedState, actions]
  );

  return <CheckoutContext.Provider value={contextValue}>{children}</CheckoutContext.Provider>;
};

export const useCheckoutContext = (): CheckoutContextType => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckoutContext must be used within a CheckoutProvider');
  }
  return context;
};
